import React, { useMemo } from 'react';
import { SwitchItem } from '../../../components';
import ColorPickerField from './ColorPickerField';
import { getTranslationKey } from '../../../modules/utils';
import { ICollectionsReducerState } from '../../../modules/reducers';
import { connect } from 'react-redux';
import * as actions from '../../../actions';
import { UncontrolledTooltip } from 'reactstrap';
import theme from '../../../assets/css/theme';
import media from '../../../assets/css/media';
import { FaUndo } from 'react-icons/fa';
import Select, { components } from 'react-select';
import { CUSTOM_PRESET_VALUE, useDesignForm } from './hooks/useDesignForm';
import styled from 'styled-components';
import { shade } from 'polished';
import { SettingsSection } from '../../../components/NewDesign/SettingsSection';
import { SegmentedControl } from '../../../components/NewDesign/SegmentedControl';
import { DESIGN_COLOR_KEYS, DesignColorKey } from '../../../modules/constants/colorPresets';
import { DEFAULT_REACTION_SET, ReactionSet } from '../../../modules/constants/reactionSets';
import { DEFAULT_ICON_SET } from '../../../modules/constants/iconSets';
import { IconSetPreview } from '../../../components/NewDesign/IconSetPreview';
import { createPillSelectStyles } from '../../../components/NewDesign/pillSelectStyles';
// @ts-ignore
import chevron from '../../../assets/img/icons/icon-chevron-down-black.svg';
import PaintIcon from '../../../assets/img/icons/paint-icon.svg?react';
import FontIcon from '../../../assets/img/icons/font-icon.svg?react';
import EmojiIcon from '../../../assets/img/icons/emoji-icon.svg?react';
import DistanceIcon from '../../../assets/img/icons/distance-icon.svg?react';
import DistanceSmallIcon from '../../../assets/img/icons/distance-icon-1.svg?react';
import DistanceMiddleIcon from '../../../assets/img/icons/distance-icon-2.svg?react';
import DistanceLargeIcon from '../../../assets/img/icons/distance-icon-3.svg?react';
import FlagIcon from '../../../assets/img/icons/flag-icon.svg?react';
import LikeIcon from '../../../assets/img/icons/like-icon.svg?react';

const COLOR_FIELDS: { key: DesignColorKey; labelKey: string }[] = [
  { key: 'primaryColor', labelKey: 'collectionDesign.galleryBackground' },
  { key: 'secondaryColor', labelKey: 'collectionDesign.galleryTextColor' },
  { key: 'uiBackgroundColor', labelKey: 'collectionDesign.uiBackground' },
  { key: 'uiTextColor', labelKey: 'collectionDesign.uiTextColor' },
  { key: 'accentColor', labelKey: 'collectionDesign.accentColor' },
  { key: 'coverTextColor', labelKey: 'collectionDesign.coverTextColor' }
];

const ColorGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 30px 20px;

  @media (max-width: ${media.mobile}) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const CustomColorsSwitchRow = styled.div<{ $hasColors: boolean }>`
  margin-top: 20px;
  padding-bottom: ${(props) => (props.$hasColors ? '35px' : 0)};
`;

const Swatches = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 8px;
  height: 32px;

  @media (max-width: ${media.mobile}) {
    gap: 4px;
    height: 20px;
  }
`;

const PresetName = styled.span`
  margin-left: 10px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: ${theme.fontSizes.body};
  white-space: nowrap;
`;

// Same outline as the gallery shop's colour swatches: a darker shade of the swatch itself, so
// white and pale colours get a visible edge while dark and saturated ones keep their own hue
// instead of a light gray ring that reads as chipped edges.
const SWATCH_OUTLINE_DARKEN = 0.14;

// polished throws on a colour it can't parse; a malformed saved value keeps its own colour
// as the outline rather than crashing the form.
const getSwatchOutline = (color: string) => {
  try {
    return shade(SWATCH_OUTLINE_DARKEN, color);
  } catch {
    return color;
  }
};

const Swatch = styled.span<{ color: string }>`
  box-sizing: border-box;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: 50%;
  background: ${(props) => props.color};
  border: 1px solid ${(props) => getSwatchOutline(props.color)};

  /* A full palette has to fit beside the chevron in a phone-width pill. */
  @media (max-width: ${media.mobile}) {
    width: 20px;
    height: 20px;
  }
`;

const ChevronImage = styled.img`
  display: block;
  width: 20px;
  height: 20px;
`;

// A palette from a backend that has not shipped a colour yet simply previews one swatch
// fewer, rather than an empty circle among the filled ones.
//
// The photographer's own variant previews the colours actually in force and says so, so the
// row is not mistaken for the untouched palette it started from.
const ColorPresetPreview = ({
  name,
  colors,
  isCustom
}: {
  name: string;
  colors: Record<DesignColorKey, string>;
  isCustom?: boolean;
}) => (
  <Swatches>
    {DESIGN_COLOR_KEYS.filter((key) => colors[key]).map((key) => (
      <Swatch key={key} color={colors[key]} />
    ))}
    <PresetName title={name}>
      {isCustom ? getTranslationKey('collectionDesign.presetCustom') : name}
    </PresetName>
  </Swatches>
);

// A section whose undo link sits in its title row rather than taking a row of its own.
const ResettableSection = styled(SettingsSection)`
  position: relative;
`;

const SectionResetAction = styled.div`
  position: absolute;
  top: 0;
  right: 0;
`;

const ReactionSetPreview = styled.div`
  display: flex;
  align-items: center;
  gap: 22px;
  min-width: 0;
  height: 30px;
`;

const ReactionEmoji = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 26px;
  width: 26px;
  height: 30px;
  font-size: 26px;
  line-height: 1;
`;

const ReactionFlag = styled(FlagIcon)<{ $filled: boolean }>`
  flex: 0 0 26px;
  width: 26px;
  height: 26px;
  color: ${(props) => props.color};

  path {
    fill: ${(props) => (props.$filled ? 'currentColor' : 'none')};
  }
`;

const ReactionPreview = ({ set }: { set: ReactionSet }) => (
  <ReactionSetPreview>
    {set.type === 'emoji'
      ? set.emojis.map((emoji, index) => (
          <ReactionEmoji key={`${emoji}-${index}`}>{emoji}</ReactionEmoji>
        ))
      : set.colors.map((color) => (
          <ReactionFlag key={color} color={color} $filled={set.filled} aria-hidden />
        ))}
  </ReactionSetPreview>
);

const DropdownIndicator = (props: any) => (
  <components.DropdownIndicator {...props}>
    <ChevronImage src={chevron} alt="" aria-hidden />
  </components.DropdownIndicator>
);

/** Shared by every dropdown in this form except the font picker, which previews fonts. */
const pillSelectStyles = createPillSelectStyles();

type Props = {
  design: ICollectionsReducerState['design'];
  templateId?: string;
  setDesign: (design: Partial<ICollectionsReducerState['design']>) => void;
  submitColor?: (...args: any[]) => void;
  availableRules: any;
  setUpgradeAccountOpenState: (obj: any) => void;
};

const DesignForm = ({
  design,
  setDesign,
  submitColor = () => {},
  templateId,
  availableRules,
  setUpgradeAccountOpenState
}: Props) => {
  const {
    useCustomColors,
    toggleCustomColors,
    defaultValues,
    fontOptions,
    colorPresetOptions,
    selectedColorPreset,
    showReactions,
    reactionSetOptions,
    selectedReactionSet,
    iconSetOptions,
    selectedIconSet,
    colorValue,
    onColorChange,
    onColorChangeComplete,
    applyPreset,
    selectFont,
    resetFont,
    changeImageSpacing,
    resetImageSpacing,
    selectReactionSet,
    selectIconSet
  } = useDesignForm({
    design,
    templateId,
    setDesign,
    submitColor,
    availableRules,
    setUpgradeAccountOpenState
  });

  // Same pill as every other dropdown, plus the font previews: the control shows the
  // chosen face and each row is drawn in the font it offers.
  const fontSelectStyles = useMemo(
    () =>
      createPillSelectStyles({
        controlExtra: { fontFamily: design.variables.font || defaultValues.font },
        optionExtra: (data) => ({ fontFamily: data.value }),
        dropdownIndicatorExtra: {
          color: '#1D1D1D',
          svg: { width: '20px', height: '20px' },
          '&:hover': { color: '#1D1D1D' }
        }
      }),
    [design.variables.font, defaultValues.font]
  );

  const undoLink = (onClick: () => void, id: string) => (
    <a
      href="/"
      style={{ cursor: 'pointer' }}
      onClick={(e) => {
        e.preventDefault();
        onClick();
      }}
    >
      <FaUndo style={{ marginLeft: '10px' }} id={id} />
      <UncontrolledTooltip placement="right" target={id}>
        {getTranslationKey('collectionDesign.undo')}
      </UncontrolledTooltip>
    </a>
  );

  return (
    <>
      <SettingsSection icon={PaintIcon} title={getTranslationKey('collectionDesign.colors')}>
        <Select
          inputId="color-preset-select"
          value={selectedColorPreset}
          options={colorPresetOptions}
          onChange={(option: any) => applyPreset(option?.value || selectedColorPreset?.value)}
          formatOptionLabel={(option: any) => (
            <ColorPresetPreview
              name={option.preset.name}
              colors={option.preset.colors}
              isCustom={option.value === CUSTOM_PRESET_VALUE}
            />
          )}
          components={{ DropdownIndicator }}
          isSearchable={false}
          styles={pillSelectStyles}
        />

        <CustomColorsSwitchRow $hasColors={useCustomColors}>
          <SwitchItem
            value={useCustomColors}
            changeCallback={toggleCustomColors}
            label={getTranslationKey('collectionDesign.customColors')}
            direction="row-reverse"
            id="use-custom-colors"
            margin="0"
          />
        </CustomColorsSwitchRow>

        {useCustomColors && (
          <ColorGrid>
            {COLOR_FIELDS.map(({ key, labelKey }) => (
              <ColorPickerField
                key={key}
                newDesign
                color={colorValue(key)}
                id={`${key}-picker`}
                label={getTranslationKey(labelKey)}
                onColorChange={(e: any) => onColorChange(key, e.hex)}
                onColorChangeComplete={(e: any) => onColorChangeComplete(key, e.hex)}
              />
            ))}
          </ColorGrid>
        )}
      </SettingsSection>

      <ResettableSection icon={FontIcon} title={getTranslationKey('collectionDesign.font')}>
        <SectionResetAction>{undoLink(resetFont, 'font')}</SectionResetAction>
        <Select
          className="basic-single"
          id="font-select"
          aria-label={getTranslationKey('collectionDesign.font')}
          components={{ DropdownIndicator }}
          value={{
            label: design.variables.font || defaultValues.font,
            value: design.variables.font || defaultValues.font
          }}
          onChange={(option: any) => selectFont(option?.value)}
          isSearchable
          name="font"
          theme={(theme) => ({
            ...theme,
            spacing: {
              ...theme.spacing,
              controlHeight: 44,
              baseUnit: 4
            }
          })}
          styles={fontSelectStyles}
          options={fontOptions}
        />
      </ResettableSection>

      <SettingsSection icon={LikeIcon} title={getTranslationKey('collectionDesign.icons')}>
        <Select
          inputId="icon-set-select"
          value={selectedIconSet}
          options={iconSetOptions}
          onChange={(option: any) => selectIconSet(option?.value || DEFAULT_ICON_SET)}
          formatOptionLabel={(option: any, { context }) => (
            <IconSetPreview
              setId={option.value}
              label={option.label}
              showCheck={context === 'menu' && option.value === selectedIconSet?.value}
            />
          )}
          components={{ DropdownIndicator }}
          isSearchable={false}
          styles={pillSelectStyles}
        />
      </SettingsSection>

      {showReactions && (
        <SettingsSection icon={EmojiIcon} title={getTranslationKey('collectionDesign.reactions')}>
          <Select
            inputId="reactions-select"
            value={selectedReactionSet}
            options={reactionSetOptions}
            onChange={(option: any) => selectReactionSet(option?.value || DEFAULT_REACTION_SET)}
            formatOptionLabel={(option: any) => <ReactionPreview set={option.set} />}
            components={{ DropdownIndicator }}
            isSearchable={false}
            styles={pillSelectStyles}
          />
        </SettingsSection>
      )}

      <ResettableSection
        icon={DistanceIcon}
        title={getTranslationKey('collectionDesign.imageSpacing')}
      >
        <SectionResetAction>{undoLink(resetImageSpacing, 'image-spacing')}</SectionResetAction>
        <SegmentedControl
          id="image-spacing-control"
          value={design.imageSpacing || defaultValues.imageSpacing}
          onChange={changeImageSpacing}
          options={[
            {
              value: 'small',
              label: getTranslationKey('collectionDesign.imageSpacingSmall'),
              Icon: DistanceSmallIcon
            },
            {
              value: 'middle',
              label: getTranslationKey('collectionDesign.imageSpacingSmallMiddle'),
              Icon: DistanceMiddleIcon
            },
            {
              value: 'large',
              label: getTranslationKey('collectionDesign.imageSpacingSmallLarge'),
              Icon: DistanceLargeIcon
            }
            // On mobile only the initial fits beside each icon: S / M / L.
          ].map((option) => ({ ...option, shortLabel: option.label.charAt(0) }))}
        />
      </ResettableSection>
    </>
  );
};

function mapStateToProps({ accessRules: { availableRules } }: { accessRules: any }) {
  return { availableRules };
}

export default connect(mapStateToProps, actions)(DesignForm);
